<template>
  <TheTemplateM
    class="p-the-page-m10"
    :is-simple-header="true"
    :step="step"
    :step-options="stepOptions"
  >
    <template #heading>認証コードの入力</template>

    <template #body>
      <div class="p-the-page-m10__message c-form-inner">
        <p class="p-the-page-m10__message__text">
          変更後メールアドレスとご自身で設定した認証コード（数字4桁）を入力してください。
          <br />認証完了後、メールアドレスが変更が完了します。
          <br />※認証を３回失敗すると、認証コードは使用できなくなります。ご注意ください。
        </p>
      </div>

      <div class="p-the-page-m10__form c-form-inner">
        <form ref="form" @submit.prevent="submit">
          <div class="p-form-group">
            <FormLabel for="id" :required="true"
              >変更後メールアドレス</FormLabel
            >
            <input
              id="id"
              v-model="id"
              type="text"
              name="id"
              :class="{ 'is-error': errors.id }"
            />
            <ValidationErrorText v-if="errors.id">{{
              errors.id
            }}</ValidationErrorText>
          </div>

          <div class="p-form-group">
            <FormLabel for="code1" :required="true"
              >認証コード（4桁）</FormLabel
            >
            <div class="p-the-page-m10__code">
              <FormCode :code="code" @set="setCode" />
              <input
                v-model="code"
                type="hidden"
                name="code"
                :class="{ 'is-error': errors.code }"
              />
              <ValidationErrorText v-if="errors.code">{{
                errors.code
              }}</ValidationErrorText>
            </div>
          </div>

          <div class="p-form-button-group">
            <div class="p-form-button-group__vertical">
              <ButtonMain type="submit" :disabled="isInvalid">認証</ButtonMain>
            </div>
          </div>

          <div class="p-form-group">
            <ValidationErrorText v-show="wrongMatch">
              IDか認証コードが設定された値と一致しません。もう一度やり直してください。
            </ValidationErrorText>
          </div>
        </form>
      </div>
    </template>
  </TheTemplateM>
</template>

<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { useForm } from "vee-validate";
import * as yup from "yup";

import TheTemplateM from "@/general/components/templates/TheTemplateM.vue";
import InputText from "@/common/components/atoms/InputText.vue";
import ButtonMain from "@/common/components/atoms/ButtonMain.vue";
import FormCode from "@/common/components/organisms/FormCode.vue";
import FormLabel from "@/common/components/organisms/FormLabel.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";

import commons from "@/common/store/modules/commons";
import LocalStorage from "@/general/utils/storage";
import AuthStorage from "@/general/utils/auth";

import {
  myPageEmailResetVerification,
  myPageEmailResetUUIDVerification
} from "@/general/store/api";

@Options({
  components: {
    TheTemplateM,
    InputText,
    ButtonMain,
    FormCode,
    FormLabel,
    ValidationErrorText
  }
})
export default class MyPageIdentityVerification extends Vue {
  step = 3;
  stepOptions = [
    { step: 1, label: "入力" },
    { step: 2, label: "メール確認" },
    { step: 3, label: "本人認証" },
    { step: 4, label: "完了" }
  ];

  breadcrumb = [
    { text: "", url: "/home", pageType: "home" },
    { text: "マイページ", url: "/my-page/", pageType: "lower" },
    { text: "メールアドレス変更", url: "", pageType: "lower" }
  ];

  id = "";
  code = "";
  uuid = "";
  wrongMatch = false;

  errors: Record<string, string> = {};
  isInvalid = false;

  get userInfo() {
    try {
      return JSON.parse(String(LocalStorage.getItem("userDetails")));
    } catch (_) {
      location.href = "/system-error/";
    }
    return null;
  }

  mounted() {
    AuthStorage.checkLogin();
  }

  async created() {
    commons.getBreadcrumb(this.breadcrumb);

    if (!LocalStorage.getItem("AccessToken")) {
      location.href = "/system-error/";
    }

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    if (urlParams.has("uuid")) {
      this.uuid = urlParams.get("uuid") as string;
    }

    await myPageEmailResetUUIDVerification({ uuid: this.uuid }).catch(
      (error: any) => {
        const { response, request } = error;

        if (
          request.responseURL.endsWith("/api/email_reset/verification_uuid/")
        ) {
          if (
            response.status === 404 &&
            response?.data?.detail === "Not found."
          ) {
            window.location.href = "/my-page/email-settings-expired/";
          } else if (response.status === 400) {
            const message = response.data?.[0] || "";
            if (message === "You have reached the maximum attempt limit.") {
              window.location.href = "/my-page/email-settings-failed/";
            } else if (message === "Email is already changed.") {
              window.location.href = "/my-page/email-settings-completed/";
            } else if (message === "URL is already expired.") {
              window.location.href = "/my-page/email-settings-expired/";
            }
          }
        }
      }
    );
  }

  async submit() {
    this.errors = {};
    this.wrongMatch = false;

    const schema = yup.object({
      id: yup.string().required("変更後メールアドレスを入力してください。"),
      code: yup.string().required("認証コード（4桁）を入力してください。")
    });

    try {
      await schema.validate(
        { id: this.id, code: this.code },
        { abortEarly: false }
      );
    } catch (err: any) {
      err.inner.forEach((e: any) => {
        this.errors[e.path] = e.message;
      });
      this.isInvalid = true;
      return;
    }

    this.isInvalid = false;

    const emailReset = await myPageEmailResetVerification({
      user: this.userInfo?.id,
      email: this.id,
      auth_code: this.code,
      uuid: this.uuid
    }).catch((error: any) => {
      this.wrongMatch = true;

      const { response, request } = error;

      if (request.responseURL.endsWith("/api/email_reset/verification/")) {
        if (response.status === 400) {
          const message = response.data?.[0] || "";
          if (message === "You have reached the maximum attempt limit.") {
            window.location.href = "/my-page/email-settings-failed/";
          } else if (message === "Email is already changed.") {
            window.location.href = "/my-page/email-settings-completed/";
          } else if (message === "URL is already expired.") {
            window.location.href = "/my-page/email-settings-expired/";
          }
        }
      }

      return;
    });

    if (emailReset) {
      window.location.href = "/my-page/email-settings-done/";
    }
  }

  setCode(code: any) {
    this.code = code;
  }
}
</script>

<style lang="scss" scoped>
.p-the-page-m10 {
  &__message {
    margin-top: rem(68);

    @include sp {
      margin-top: rem(28);
    }

    &__text {
      // TODO: irregular
      color: #4f4f4f;
      font-size: rem(14);
      line-height: (24/14);
    }
  }

  &__form {
    margin-top: rem(24);

    @include sp {
      margin-top: rem(16);
    }
  }

  &__code {
    margin-top: rem(12);
  }
}
</style>
